/* eslint-disable max-lines */
import type {
    HTMLAttributes,
    Ref,
    ReactElement,
    ReactNode,
    UIEvent,
} from 'react';
import {
    forwardRef,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import type { MaterialAutocompleteProps } from './MaterialAutocomplete.types';
import type {
    DisableClearableType,
    FreeSoloType,
    MultipleType,
    NonValueOption,
    ValueType,
} from './utils';
import type {
    ExtendedMaterial,
    ExtendedMaterialOnTheFly,
    Material,
    MaterialOnTheFly,
} from '../../gql';
import type { AutocompleteRenderGroupParams } from '@mui/material/Autocomplete';

import { useKeyboardShortcut } from '@bestseller-bit/frontend-community.hooks.use-keyboard-shortcut';
import Box from '@mui/material/Box';

import {
    getMaterialOnTheFlySearchableText,
    getOptionLabelFn,
    isExtendedMaterialOnTheFly,
    isMaterialOnTheFly,
} from '../../utils';
import { BomAutocomplete } from '../BomAutocomplete';

import { buildMaterialOptions } from './buildMaterialOptions';
import { MaterialAutocompleteGroup } from './MaterialAutocompleteGroup';
import { MaterialValue } from './MaterialValue';
import { useMaterialScrollSentinels } from './useMaterialScrollSentinels';
import { useMaterialSearchState } from './useMaterialSearchState';
import {
    fillerMaterialOnTheFly,
    filterOptionsFn,
    generateOptionKey,
    getOptionDisabledFn,
    groupByFn,
    isOptionEqualToValueFn,
    renderOptionFn,
    sortMaterialsByMaterialSubcategoryName,
} from './utils';

const MaterialAutocomplete = forwardRef(
    (
        {
            options: materials,
            isLoading,
            isSearching,
            isStreaming,
            isCustomAllowed,
            isGroupedByMaterialSubcategory = false,
            subcategoryIdByName,
            groupLoadingStates,
            disableValueRender = false,
            isSearchActiveOverride = false,
            onAddClick,
            onIntersection,
            onGroupExpand,
            onChange,
            onEditClick,
            ...autoCompleteProps
        }: MaterialAutocompleteProps,
        ref
    ): ReactElement => {
        /**
         * MUI's Autocomplete resets the listbox scrollTop to 0 from its own internal
         * effect whenever a grouped/paginated options update can't be recognized as a
         * pure append — which our contiguous-group requirement makes unavoidable for
         * any group that isn't the very last thing in the list. Both pagination and a
         * group's initial full-page fetch (after expanding) trigger this, and scrolling
         * fast can trigger several of these updates in quick succession.
         */
        const listboxRef = useRef<HTMLUListElement | null>(null);
        const lastScrollTopRef = useRef(0);
        const isScrollGuardActiveRef = useRef(false);
        const scrollGuardTimeoutRef = useRef<number | null>(null);
        const removeScrollGuardRef = useRef<(() => void) | null>(null);
        /**
         * Expanding a group can change its rendered row count drastically (from a
         * single collapsed placeholder to many material rows), which can shift the
         * clicked group away from where the user was looking — regardless of what
         * MUI's own scrollTop handling does. Rather than guessing the "correct"
         * scrollTop number, we anchor to the actual clicked header element and, once
         * the new rows have rendered, restore that same header to the exact viewport
         * position it was in before the click.
         */
        const expandAnchorRef = useRef<{
            element: HTMLElement;
            viewportTop: number;
        } | null>(null);
        const wasAnyExpandedGroupLoadingRef = useRef(false);

        const [hoveredOptionKey, setHoveredOptionKey] = useState<string | null>(
            null
        );
        const [focusedOptionKey, setFocusedOptionKey] = useState<string | null>(
            null
        );

        /**
         * The set of subcategory group names that are currently expanded.
         * Groups default to collapsed (empty set), and multiple groups can be open at once.
         * The set is reset whenever the dropdown closes, so it reopens fully collapsed.
         */
        const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
            new Set()
        );

        const {
            searchInputValue,
            setSearchInputValue,
            isSearchActive: isTypedSearchActive,
            isSearchPending,
        } = useMaterialSearchState({
            materials,
            controlledInputValue: autoCompleteProps.inputValue,
        });

        // Shortcut logic
        // the drawer is not open when we open the dropdown so we dont implement this shortcut in the drawer itself for now
        useKeyboardShortcut({
            keyCombo: { code: 'KeyE', altKey: true },
            callback: (event) => {
                event.preventDefault(); // Prevents opening the browser menu
                // Prefer focusedOption over hoveredOption for shortcut
                const activeOptionKey = focusedOptionKey ?? hoveredOptionKey;
                if (activeOptionKey) {
                    const activeOption = materials.find(
                        (option) =>
                            generateOptionKey(option) === activeOptionKey
                    );
                    if (activeOption && onEditClick) {
                        onEditClick(activeOption);
                    }
                }
            },
        });

        /**
         * The caller may already know a search is active (e.g. it just triggered one
         * programmatically) before the user has typed anything into this component,
         * so we OR it with the internally tracked typed-search state.
         */
        const isSearchActive = isTypedSearchActive || isSearchActiveOverride;

        const inputRef: Ref<HTMLInputElement> | undefined =
            autoCompleteProps.textfieldProps?.inputRef;
        const placeholderValue =
            autoCompleteProps.textfieldProps?.placeholder ?? 'Select material';

        /**
         * When added to the options array,
         * this object will render the "add material on the fly" button option.
         * We want to render it as an option in the autocomplete,
         * so the user can select it with the arrow keys or click on it.
         * It is not a selectable value though, so we give it the right type.
         */
        const addMofOption: NonValueOption = useMemo(
            () => ({
                nonValueOptionId: 'add-mof-plus-button',
                label: 'Material on the fly',
                onClick: (): void => {
                    onAddClick();
                },
            }),
            [onAddClick]
        );

        /**
         * Here we define two arrays by splitting the materials into materials and material on the fly options.
         */
        const { materialOnTheFlyOptions, materialOptions } = useMemo(() => {
            const newMaterialOnTheFlyOptions: Array<
                ExtendedMaterialOnTheFly | MaterialOnTheFly
            > = [];
            const newMaterialOptions: Array<ExtendedMaterial | Material> = [];

            // For each material, check in which array it belongs and push it to the right one.
            materials.forEach((material) => {
                const isMof =
                    isMaterialOnTheFly(material) ||
                    isExtendedMaterialOnTheFly(material);
                if (isMof) {
                    newMaterialOnTheFlyOptions.push(material);
                } else {
                    newMaterialOptions.push(material);
                }
            });

            /**
             * If there are no material on the fly options, we add a filler option to the array so that the materials on the fly group is not filtered out.
             * This is useful when the user has no materials on the fly, but we still want to show the "add material on the fly" button and the message that it's empty.
             * We also sort the materials by their subcategory name.
             */
            return {
                materialOnTheFlyOptions: newMaterialOnTheFlyOptions.length
                    ? newMaterialOnTheFlyOptions
                    : [fillerMaterialOnTheFly],
                materialOptions: newMaterialOptions.toSorted(
                    sortMaterialsByMaterialSubcategoryName
                ),
            };
        }, [materials]);

        // Derive subcategory names from the subcategoryIdByName map (browse mode).
        const materialSubcategoryNames = useMemo(
            () =>
                subcategoryIdByName
                    ? Object.keys(subcategoryIdByName)
                    : undefined,
            [subcategoryIdByName]
        );

        // Now gather all the options into a single array, which we provide to the autocomplete.
        const options: Array<ValueType> = useMemo(
            () =>
                buildMaterialOptions({
                    isSearching,
                    isSearchPending,
                    isSearchActive,
                    isLoading,
                    isCustomAllowed,
                    isGroupedByMaterialSubcategory,
                    materials,
                    materialOptions,
                    materialOnTheFlyOptions,
                    materialSubcategoryNames,
                    expandedGroups,
                    addMofOption,
                }),
            [
                isSearching,
                isSearchPending,
                isSearchActive,
                isLoading,
                isCustomAllowed,
                isGroupedByMaterialSubcategory,
                materials,
                materialOptions,
                materialOnTheFlyOptions,
                materialSubcategoryNames,
                expandedGroups,
                addMofOption,
            ]
        );

        /**
         * The set of group names that have no matching options.
         * Used by the group component to show "No materials found" under the right headers.
         * Includes any subcategory groups that have no matching materials.
         */
        const emptySubcategoryNamesSet = useMemo((): Set<string> => {
            const names = new Set<string>();

            // Mark subcategory groups with no materials
            if (isGroupedByMaterialSubcategory && materialSubcategoryNames) {
                const subcategoriesWithMaterials = new Set(
                    materialOptions.map(
                        (material) =>
                            material.materialSubcategory.materialSubcategoryName
                    )
                );
                materialSubcategoryNames.forEach((name) => {
                    if (!subcategoriesWithMaterials.has(name)) {
                        names.add(name);
                    }
                });
            }

            return names;
        }, [
            isGroupedByMaterialSubcategory,
            materialOptions,
            materialSubcategoryNames,
        ]);

        /**
         * Whether the user is actively searching but nothing matched — neither a regular
         * material (filtered by the backend) nor a material on the fly (filtered on the
         * frontend by matching its rendered text). Used to show "No materials found".
         */
        const hasNoMaterialSearchResults = useMemo(() => {
            if (!isSearchActive || isSearching || isSearchPending || isLoading)
                return false;
            if (materialOptions.length > 0) return false;

            const searchQuery = (
                searchInputValue.trim() ||
                (autoCompleteProps.inputValue ?? '').trim()
            ).toLowerCase();

            const hasMatchingMaterialOnTheFly = materialOnTheFlyOptions.some(
                (materialOnTheFly) =>
                    materialOnTheFly.materialOnTheFlyPublicId !==
                        fillerMaterialOnTheFly.materialOnTheFlyPublicId &&
                    getMaterialOnTheFlySearchableText(
                        materialOnTheFly
                    ).includes(searchQuery)
            );

            return !hasMatchingMaterialOnTheFly;
        }, [
            isSearchActive,
            isSearching,
            isSearchPending,
            isLoading,
            materialOptions,
            materialOnTheFlyOptions,
            searchInputValue,
            autoCompleteProps.inputValue,
        ]);

        // slotProps.listbox can theoretically be a function per MUI's typing, but we
        // only ever expect callers to pass a plain object here.
        const callerListboxProps = autoCompleteProps.slotProps?.listbox as
            | (HTMLAttributes<HTMLUListElement> & {
                  ref?: Ref<HTMLUListElement>;
              })
            | undefined;
        const callerListboxRef = callerListboxProps?.ref;

        /**
         * The timer-based guard window is fine for a click/intersection trigger, but
         * the fetch it's meant to cover (a group's initial full page, or its next
         * page) can take arbitrarily long on a slow connection — long enough that no
         * other render happens in between to re-extend a fixed timeout, and the data
         * arrives as a plain prop update rather than through our own handlers. Tying
         * activation directly to the loading boolean (in an effect, so it only runs
         * on committed renders, not ones React discards) keeps the guard active for
         * as long as a fetch is genuinely in flight, however long that takes, and
         * only starts the short decay once loading actually finishes — covering the
         * completion render where the real data (and MUI's possible reset) lands.
         */
        const isAnyExpandedGroupLoading =
            !!subcategoryIdByName &&
            !!groupLoadingStates &&
            Array.from(expandedGroups).some((groupName) => {
                const subcategoryId = subcategoryIdByName[groupName];
                return subcategoryId
                    ? groupLoadingStates[subcategoryId]
                    : false;
            });

        /**
         * Marks the listbox as "scroll-sensitive" for a short window. Called every time
         * a pagination or group-expand fetch is triggered, extending (not restarting
         * from scratch) the window so rapid, overlapping triggers stay covered.
         */
        const activateScrollGuard = useCallback((): void => {
            isScrollGuardActiveRef.current = true;
            if (scrollGuardTimeoutRef.current !== null) {
                window.clearTimeout(scrollGuardTimeoutRef.current);
            }
            scrollGuardTimeoutRef.current = window.setTimeout(() => {
                isScrollGuardActiveRef.current = false;
                scrollGuardTimeoutRef.current = null;
            }, 1000);
        }, []);

        /**
         * Installs a scrollTop interceptor on the listbox node, once per mounted node.
         * Any write of exactly 0 is redirected to the real position while the guard is
         * active. Native scrolling (mouse wheel, trackpad, etc.) never goes through
         * this setter, so it's completely unaffected. Installing once and toggling
         * `isScrollGuardActiveRef` (instead of reinstalling per options change) avoids
         * any gap where a reset could slip through unguarded between two fast,
         * back-to-back pagination triggers.
         */
        const installScrollGuard = useCallback(
            (node: HTMLUListElement): (() => void) => {
                let prototype = Object.getPrototypeOf(node) as object | null;
                let original: PropertyDescriptor | undefined;
                while (prototype && !original) {
                    original = Object.getOwnPropertyDescriptor(
                        prototype,
                        'scrollTop'
                    );
                    prototype = Object.getPrototypeOf(prototype) as
                        | object
                        | null;
                }
                const noopTeardown = (): void => {
                    // Descriptor lookup failed, so there's nothing to tear down.
                };
                if (!original?.get || !original.set) return noopTeardown;
                const descriptor = original as Required<
                    Pick<PropertyDescriptor, 'get' | 'set'>
                >;

                Object.defineProperty(node, 'scrollTop', {
                    configurable: true,
                    get(): number {
                        return descriptor.get.call(node) as number;
                    },
                    set(value: number): void {
                        // MUI's "scroll highlighted option into view" logic can compute
                        // a negative target (offsetTop minus padding) when the
                        // highlighted option is near the top of its group — browsers
                        // clamp that to 0 natively, bypassing an `=== 0` check, so any
                        // non-positive write must be redirected too.
                        const shouldRedirect =
                            value <= 0 && isScrollGuardActiveRef.current;
                        descriptor.set.call(
                            node,
                            shouldRedirect ? lastScrollTopRef.current : value
                        );
                    },
                });

                return (): void => {
                    Reflect.deleteProperty(node, 'scrollTop');
                };
            },
            []
        );

        const handleListboxRef = useCallback(
            (node: HTMLUListElement | null): void => {
                listboxRef.current = node;
                removeScrollGuardRef.current?.();
                removeScrollGuardRef.current = node
                    ? installScrollGuard(node)
                    : null;
            },
            [installScrollGuard]
        );

        const handleToggleGroup = useCallback(
            (groupName: string, headerElement?: HTMLElement): void => {
                if (headerElement) {
                    expandAnchorRef.current = {
                        element: headerElement,
                        viewportTop: headerElement.getBoundingClientRect().top,
                    };
                }
                // Computed from the current state up front (not inside the updater
                // below), since calling a different component's setState from within
                // our own updater is an anti-pattern React warns about.
                const isExpanding = !expandedGroups.has(groupName);
                setExpandedGroups((previousExpandedGroups) => {
                    const nextExpandedGroups = new Set(previousExpandedGroups);
                    if (isExpanding) {
                        nextExpandedGroups.add(groupName);
                    } else {
                        nextExpandedGroups.delete(groupName);
                    }
                    return nextExpandedGroups;
                });
                if (isExpanding) {
                    // Notify the provider to fetch the full first page for this group.
                    const subcategoryId = subcategoryIdByName?.[groupName];
                    if (subcategoryId) {
                        activateScrollGuard();
                        onGroupExpand?.(subcategoryId);
                    }
                }
            },
            [
                expandedGroups,
                subcategoryIdByName,
                onGroupExpand,
                activateScrollGuard,
            ]
        );

        const handleHover = (option: ValueType | null): void => {
            setHoveredOptionKey(option ? generateOptionKey(option) : null);
        };

        const handleListboxScroll = useCallback(
            (event: UIEvent<HTMLUListElement>): void => {
                lastScrollTopRef.current = event.currentTarget.scrollTop;
            },
            []
        );

        const mergedListboxRef = useCallback(
            (node: HTMLUListElement | null): void => {
                handleListboxRef(node);
                if (typeof callerListboxRef === 'function') {
                    callerListboxRef(node);
                } else if (callerListboxRef) {
                    callerListboxRef.current = node;
                }
            },
            [handleListboxRef, callerListboxRef]
        );
        const handleMergedListboxScroll = useCallback(
            (event: UIEvent<HTMLUListElement>): void => {
                callerListboxProps?.onScroll?.(event);
                handleListboxScroll(event);
            },
            [callerListboxProps, handleListboxScroll]
        );

        const handleIntersection = useCallback(
            (subcategoryId?: string): void => {
                activateScrollGuard();
                onIntersection?.(subcategoryId);
            },
            [onIntersection, activateScrollGuard]
        );

        // Deviates from the usual "custom hooks before callbacks" order: this hook
        // genuinely depends on handleIntersection above, which itself depends on
        // activateScrollGuard, so it can't be declared any earlier.
        const { getSentinelRef } = useMaterialScrollSentinels({
            isGroupedByMaterialSubcategory,
            isSearchActive,
            materialOptions,
            expandedGroups,
            subcategoryIdByName,
            onIntersection: handleIntersection,
        });

        useEffect(
            () => (): void => {
                if (scrollGuardTimeoutRef.current !== null) {
                    window.clearTimeout(scrollGuardTimeoutRef.current);
                }
                removeScrollGuardRef.current?.();
            },
            []
        );

        useEffect(() => {
            if (isAnyExpandedGroupLoading) {
                isScrollGuardActiveRef.current = true;
                if (scrollGuardTimeoutRef.current !== null) {
                    window.clearTimeout(scrollGuardTimeoutRef.current);
                    scrollGuardTimeoutRef.current = null;
                }
            } else if (wasAnyExpandedGroupLoadingRef.current) {
                activateScrollGuard();
            }
            wasAnyExpandedGroupLoadingRef.current = isAnyExpandedGroupLoading;
        }, [isAnyExpandedGroupLoading, activateScrollGuard]);

        useLayoutEffect(() => {
            const anchor = expandAnchorRef.current;
            expandAnchorRef.current = null;
            if (!anchor || !listboxRef.current) return;

            const newViewportTop = anchor.element.getBoundingClientRect().top;
            const delta = newViewportTop - anchor.viewportTop;
            if (delta !== 0) {
                listboxRef.current.scrollTop += delta;
            }
        }, [options]);

        /**
         * A group can briefly render with far less content than it will have once
         * loaded (e.g. right after expanding, before its materials or even the loading
         * skeleton show up), shrinking the listbox's scrollHeight below the current
         * scrollTop. Browsers then clamp scrollTop on their own as part of layout —
         * this never goes through the scrollTop setter our guard above intercepts, so
         * it needs its own fix. Re-applying the protected position in a layout effect
         * (which runs right after layout, before paint) corrects that clamp before the
         * user ever sees it.
         */
        useLayoutEffect(() => {
            if (isScrollGuardActiveRef.current && listboxRef.current) {
                listboxRef.current.scrollTop = lastScrollTopRef.current;
            }
        }, [options]);

        return (
            <BomAutocomplete<
                ValueType,
                MultipleType,
                DisableClearableType,
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-arguments
                FreeSoloType
            >
                {...autoCompleteProps}
                ref={ref}
                textfieldProps={{
                    inputRef: inputRef,
                    placeholder: placeholderValue,
                    ...autoCompleteProps.textfieldProps, // Spread existing textfieldProps
                }}
                slotProps={{
                    ...autoCompleteProps.slotProps,
                    // Caller-provided slotProps.listbox (styling, aria attributes, ref,
                    // onScroll, etc.) is preserved; ref and onScroll are composed with
                    // ours rather than replaced.
                    listbox: {
                        ...callerListboxProps,
                        ref: mergedListboxRef,
                        onScroll: handleMergedListboxScroll,
                    },
                }}
                options={options}
                noOptionsText={
                    isSearching || isSearchPending ? '' : 'No materials found'
                }
                getOptionKey={generateOptionKey}
                getOptionLabel={getOptionLabelFn}
                getOptionDisabled={getOptionDisabledFn}
                isOptionEqualToValue={isOptionEqualToValueFn}
                filterOptions={(optionsToFilter, state) =>
                    // Materials are filtered by the backend and always pass the client-side
                    // filter, so applying it never removes them (no flicker). MOFs, however,
                    // are filtered entirely on the frontend by matching their rendered text,
                    // so we always run the filter to keep MOF results in sync with the query.
                    filterOptionsFn({
                        unfilteredOptions: optionsToFilter,
                        state,
                        isGroupedByMaterialSubcategory,
                    })
                }
                // We add the onHighlightChange to set the focused option
                renderOption={(props, option) => {
                    // In browse mode the sentinel sits on the last option of each expanded
                    // group; in search/flat mode it sits on the very last renderable option.
                    const sentinelRef = getSentinelRef({ option, options });

                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                            onMouseEnter={() => {
                                handleHover(option);
                            }}
                            onMouseLeave={() => {
                                handleHover(null);
                            }}
                        >
                            {renderOptionFn({
                                props: {
                                    ...props,
                                    ref: sentinelRef,
                                },
                                option,
                                isStreaming,
                                focusedOptionKey,
                                searchQuery:
                                    searchInputValue ||
                                    (autoCompleteProps.inputValue ?? ''),
                                onEditClick: (event) => {
                                    // This is the onEditClick function passed to MOF list items
                                    event.stopPropagation();
                                    // Prefer focusedOption if set, otherwise hoveredOption
                                    const key =
                                        focusedOptionKey ??
                                        hoveredOptionKey ??
                                        generateOptionKey(option);
                                    const activeOption = materials.find(
                                        (candidateOption) =>
                                            generateOptionKey(
                                                candidateOption
                                            ) === key
                                    );
                                    if (activeOption && onEditClick) {
                                        onEditClick(activeOption);
                                    }
                                },
                            })}
                        </Box>
                    );
                }}
                groupBy={(option) =>
                    groupByFn({
                        option,
                        shouldGroupByMaterialSubcategory:
                            isGroupedByMaterialSubcategory,
                    })
                }
                renderGroup={(
                    params: AutocompleteRenderGroupParams
                ): ReactNode => {
                    // Look up the subcategoryId for this group name to retrieve per-group loading state.
                    const subcategoryId = subcategoryIdByName?.[params.group];
                    const isGroupLoading =
                        !!subcategoryId &&
                        !!groupLoadingStates?.[subcategoryId];

                    return (
                        <MaterialAutocompleteGroup
                            {...params}
                            key={params.key}
                            isOnlyGroup={options.length === 1}
                            isCustomAllowed={isCustomAllowed}
                            isEmptySubcategory={emptySubcategoryNamesSet.has(
                                params.group
                            )}
                            isCollapsible={isGroupedByMaterialSubcategory}
                            isExpanded={
                                isSearchActive ||
                                expandedGroups.has(params.group)
                            }
                            isGroupLoading={isGroupLoading}
                            hasNoMaterialSearchResults={
                                hasNoMaterialSearchResults
                            }
                            onToggleExpand={(headerElement: HTMLElement) => {
                                handleToggleGroup(params.group, headerElement);
                            }}
                        />
                    );
                }}
                renderValue={(valueToRender): ReactNode => {
                    if (disableValueRender) return null;

                    // Do not render anything if the value is a non-value option.
                    if ('nonValueOptionId' in valueToRender) return null;

                    // Placeholders are never selectable, but guard defensively.
                    if ('emptySubcategoryPlaceholderId' in valueToRender)
                        return null;
                    if ('collapsedSubcategoryPlaceholderId' in valueToRender)
                        return null;

                    return (
                        <Box
                            // We add this box with styling in order to avoid the overflow of the text
                            sx={{
                                paddingRight: '44px',
                                wordBreak: 'break-word',
                                height: 1, // Make sure the container is the same height as the input
                            }}
                        >
                            <MaterialValue
                                value={valueToRender}
                                isDisabled={autoCompleteProps.disabled}
                                isImageLoading={isStreaming}
                            />
                        </Box>
                    );
                }}
                onHighlightChange={(_, option) => {
                    setFocusedOptionKey(
                        option ? generateOptionKey(option) : null
                    );
                }}
                onInputChange={(event, value, reason) => {
                    // Only track user-typed input as a search. MUI fires 'reset' when
                    // the dropdown opens to display the selected value's label in the
                    // input — this is not a user search and must not activate the
                    // search-pending state (which would hide all loaded materials).
                    if (reason !== 'reset') {
                        setSearchInputValue(value);
                    }
                    autoCompleteProps.onInputChange?.(event, value, reason);
                }}
                // We need to reset these values when closing the autocomplete to trigger again the edit action
                onClose={(event, reason) => {
                    setFocusedOptionKey(null);
                    setHoveredOptionKey(null);
                    // Reopen fully collapsed and clear any active search
                    setExpandedGroups(new Set());
                    setSearchInputValue('');
                    autoCompleteProps.onClose?.(event, reason);
                }}
                onChange={(event, value) => {
                    if (value && 'collapsedSubcategoryPlaceholderId' in value) {
                        // Collapsed placeholders are not selectable; ignore them.
                        event.preventDefault();
                        event.stopPropagation();
                    } else if (value && 'nonValueOptionId' in value) {
                        // If the user clicks on the "add MOF" option, we prevent the default behavior and call the onClick function.
                        event.preventDefault();
                        event.stopPropagation();
                        value.onClick?.();
                    } else {
                        // Otherwise, we call the onChange function passed to the component with the actual value.
                        onChange?.(event, value);
                    }
                }}
            />
        );
    }
);

MaterialAutocomplete.displayName = 'MaterialAutocomplete';

export { MaterialAutocomplete, sortMaterialsByMaterialSubcategoryName };
export type { MaterialAutocompleteProps };
